import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  echoScoreDisplay,
  passesMandate,
} from '@/config/indexThresholds';
import {
  recommendationTextClass,
  scriptDisplayStatus,
} from '@/config/recommendation';
import { formatScriptId } from '@/utils/formatIndexId';
import type { WorkflowScriptListItem } from '@/types/workflow';

type ScriptRegistryTableProps = {
  scripts: WorkflowScriptListItem[];
  onSelect: (script: WorkflowScriptListItem) => void;
  onDelete?: (script: WorkflowScriptListItem) => void;
  className?: string;
};

export default function ScriptRegistryTable({
  scripts,
  onSelect,
  onDelete,
  className,
}: ScriptRegistryTableProps) {
  if (!scripts.length) {
    return (
      <div className={cn('ni-panel p-4', className)}>
        <p className="ni-label mb-3">Ingested screenplays</p>
        <p className="text-sm text-white/40 font-sans">No screenplays ingested for this mandate.</p>
      </div>
    );
  }

  return (
    <div className={cn('ni-panel overflow-hidden', className)}>
      <div className="px-4 py-3 border-b border-[#2a2a2a]">
        <p className="ni-label">Ingested screenplays</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm font-sans">
          <thead>
            <tr className="border-b border-[#2a2a2a] text-[10px] uppercase tracking-wider text-white/35">
              <th className="px-4 py-2 font-medium">ID</th>
              <th className="px-4 py-2 font-medium">Title</th>
              <th className="px-4 py-2 font-medium text-right">Recommendation</th>
              <th className="px-4 py-2 font-medium text-right">Echo</th>
              <th className="px-4 py-2 font-medium text-right">Mandate</th>
              {onDelete && <th className="px-4 py-2 font-medium text-right sr-only">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {scripts.map((script) => {
              const id = formatScriptId(script.analysis_session_id || script.session_id);
              const status = scriptDisplayStatus(script);
              const score = echoScoreDisplay(script.overall_score);
              const mandatePct = script.mandate_match_pct;
              const mandatePass = passesMandate(mandatePct);
              return (
                <tr
                  key={script.session_id}
                  className="ni-table-row cursor-pointer"
                  onClick={() => onSelect(script)}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-white/50">{id}</td>
                  <td className="px-4 py-2.5 text-white truncate max-w-[200px]">
                    {script.script_title || 'Untitled'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span
                      className={cn(
                        'text-[10px] font-mono uppercase tracking-wider',
                        recommendationTextClass(status),
                      )}
                    >
                      {status}
                    </span>
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right font-mono tabular-nums',
                      score != null ? 'text-index-cleared' : 'text-white/35',
                    )}
                  >
                    {score != null ? score.toFixed(1) : 'N/A'}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right font-mono tabular-nums',
                      mandatePct != null
                        ? mandatePass
                          ? 'text-index-cleared'
                          : 'text-index-rewrite'
                        : 'text-white/35',
                    )}
                  >
                    {mandatePct != null ? `${mandatePct}%` : 'N/A'}
                  </td>
                  {onDelete && (
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        aria-label={`Delete ${script.script_title || 'script'}`}
                        title="Delete script (irreversible)"
                        className="text-white/30 hover:text-index-rewrite transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(script);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
